/**
 * The user web app's entry point.
 *
 * ORDER MATTERS HERE. The host is installed before anything that logs, the
 * global error handlers before the first render, and the 401 handler before the
 * router mounts — a session that has already lapsed produces its 401 on the very
 * first query, and without the handler in place that 401 has nowhere to go.
 */

import "./styles/index.css"

import {
	analytics,
	ANALYTICS_ON,
	createWebProviders,
	restoreAnalyticsConsent,
} from "@anthra/platform/analytics"
import { installHost } from "@anthra/platform/host"
import { webHost } from "@anthra/platform/host/web"
import { installGlobalErrorHandlers } from "@anthra/platform/logger"
import { RootErrorBoundary } from "@anthra/ui/error-boundary"
import { QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"

import { ConsentLayer } from "@/components/ConsentLayer"
import { createQueryClient } from "@/lib/query-client"
import { installUnauthenticatedHandler } from "@/lib/unauthenticated"

import { routeTree } from "./routeTree.gen"

// FIRST. Everything below this line may log, and the logger asks the host for
// its configuration.
installHost(webHost())

installGlobalErrorHandlers()

// Analytics is switched off (ANALYTICS_ON in @anthra/platform/analytics): no
// providers, no stored consent read, and no consent screen below.
if (ANALYTICS_ON) analytics.register(...createWebProviders())
const consent = ANALYTICS_ON ? await restoreAnalyticsConsent() : undefined
if (ANALYTICS_ON) analytics.setConsent(consent?.analytics ?? false)

const queryClient = createQueryClient()

const router = createRouter({
	routeTree,
	context: { queryClient },
	// Long enough that a fast navigation does not flash a spinner, which reads as
	// slower than no spinner at all.
	defaultPendingMs: 300,
	defaultPreload: "intent",
})

// The path is read from the ROUTER rather than from `location`, so the held
// destination is this app's own route with its search parameters — which is what
// the user is returned to after OTP sign-in (US-00-002.26).
installUnauthenticatedHandler(queryClient, () => router.state.location.href)

router.subscribe("onLoad", () => {
	analytics.setScreen(router.state.location.pathname)
})

declare module "@tanstack/react-router" {
	// An INTERFACE, and it must be. This is module augmentation: TanStack Router
	// declares `Register` as an interface, and only interfaces merge.
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Register {
		router: typeof router
	}
}

const container = document.getElementById("root")
if (!container) throw new Error("#root is missing from index.html")

createRoot(container).render(
	<StrictMode>
		<RootErrorBoundary>
			<QueryClientProvider client={queryClient}>
				{ANALYTICS_ON ? <ConsentLayer initialConsent={consent?.analytics} /> : null}
				<RouterProvider router={router} />
			</QueryClientProvider>
		</RootErrorBoundary>
	</StrictMode>,
)
